import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import { useTranslation } from 'react-i18next';
import {
  PaperAirplaneIcon,
  TrashIcon,
  ChatBubbleLeftRightIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import apiClient, { exportAPI } from '../services/api';
import { useActivitiesStore } from '../store/activitiesStore';
import { ExportDialog } from '../components/ui/ExportDialog';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

interface TravelCategory {
  id: string;
  name: string;
  description: string;
}

interface QuestionResult {
  session_id: string;
  category: string;
  question: string;
  answer: string;
  key_points: string[];
  action_items: string[];
  related_topics: string[];
  disclaimers: string[];
  follow_up_suggestions: string[];
  timestamp: Date;
}

// Default categories for travel questions
const defaultCategories: TravelCategory[] = [
  { id: 'destinations', name: 'Destinations', description: 'Best places to visit, hidden gems, and travel recommendations' },
  { id: 'visa-documents', name: 'Visa & Documents', description: 'Visa requirements, passport info, and travel documents' },
  { id: 'transportation', name: 'Transportation', description: 'Flights, trains, buses, and getting around' },
  { id: 'accommodation', name: 'Accommodation', description: 'Hotels, hostels, Airbnb, and where to stay' },
  { id: 'food-dining', name: 'Food & Dining', description: 'Local cuisine, restaurants, and food tips' },
  { id: 'budget', name: 'Budget', description: 'Trip costs, saving money, and budget planning' },
  { id: 'safety', name: 'Safety', description: 'Travel safety tips, scams to avoid, and emergency info' },
  { id: 'culture', name: 'Culture', description: 'Local customs, etiquette, and cultural experiences' },
  { id: 'activities', name: 'Activities', description: 'Things to do, tours, and attractions' },
  { id: 'weather', name: 'Weather', description: 'Best times to visit, seasonal info, and climate' },
  { id: 'health', name: 'Health', description: 'Vaccinations, travel health tips, and medical info' },
  { id: 'packing', name: 'Packing', description: 'What to pack, luggage tips, and essentials' },
  { id: 'communication', name: 'Communication', description: 'Language tips, SIM cards, and staying connected' },
  { id: 'insurance', name: 'Insurance', description: 'Travel insurance, coverage, and claims' },
];

// Popular topics by category
const TOPICS_BY_CATEGORY: Record<string, string[]> = {
  destinations: [
    'What are the best budget-friendly destinations in Southeast Asia?',
    'Top 5 hidden gem destinations in Europe?',
    'Best places to visit in Japan for first-timers?',
    'Where to go for a romantic getaway in Italy?',
    'Most beautiful beaches in the Caribbean?',
  ],
  'visa-documents': [
    'Do I need a visa to visit Thailand?',
    'How long does passport renewal take?',
    'What documents do I need for a Schengen visa?',
    'Can I get a visa on arrival in Bali?',
    'What is an e-visa and which countries offer them?',
  ],
  transportation: [
    'Is it cheaper to fly or take the train in Europe?',
    'How to use the subway in Tokyo?',
    'Best apps for booking cheap flights?',
    'Should I rent a car in Iceland?',
    'How to get from the airport to city center?',
  ],
  accommodation: [
    'Hotel vs Airbnb: which is better for families?',
    'How to find cheap accommodation in Paris?',
    'What are capsule hotels in Japan?',
    'Best areas to stay in Barcelona?',
    'How to choose safe hostels for solo travelers?',
  ],
  'food-dining': [
    'Must-try street food in Bangkok?',
    'How to eat safely in developing countries?',
    'Best food tours in Mexico City?',
    'Tipping etiquette in different countries?',
    'How to find authentic local restaurants abroad?',
  ],
  budget: [
    'How much does a week in Bali cost?',
    'Budget travel tips for Europe?',
    'How to save money on flights?',
    'Daily budget for traveling in Japan?',
    'Best free things to do in London?',
  ],
  safety: [
    'Is it safe to travel alone in South America?',
    'Common travel scams to watch out for?',
    'How to keep money safe while traveling?',
    'Emergency contacts for travelers in Europe?',
    'What to do if your passport is stolen abroad?',
  ],
  culture: [
    'Cultural etiquette in Japan?',
    'What to wear when visiting temples in Asia?',
    'Gift-giving customs around the world?',
    'How to greet people in different cultures?',
    'Cultural do\'s and don\'ts in the Middle East?',
  ],
  activities: [
    'Best hiking trails in New Zealand?',
    'Top water sports in Hawaii?',
    'Must-see museums in Paris?',
    'Best safari experiences in Africa?',
    'Top adventure activities in Costa Rica?',
  ],
  weather: [
    'Best time to visit Greece?',
    'When is monsoon season in Thailand?',
    'What is the best month to visit Iceland?',
    'Dry season vs wet season in Bali?',
    'Winter travel tips for Northern Europe?',
  ],
  health: [
    'What vaccinations do I need for Africa?',
    'How to prevent altitude sickness in Peru?',
    'Travel health insurance recommendations?',
    'How to avoid getting sick from food abroad?',
    'Jet lag remedies that actually work?',
  ],
  packing: [
    'What to pack for a 2-week Europe trip?',
    'Essential items for tropical destinations?',
    'Carry-on only packing tips?',
    'Best travel accessories for long flights?',
    'How to pack for multiple climates?',
  ],
  communication: [
    'Best translation apps for travelers?',
    'Should I get a local SIM card or use eSIM?',
    'Basic phrases to learn before visiting Japan?',
    'Best VPN for traveling abroad?',
    'How to stay connected while traveling?',
  ],
  insurance: [
    'Is travel insurance worth it?',
    'What does travel insurance typically cover?',
    'Best travel insurance for adventure activities?',
    'How to file a travel insurance claim?',
    'Do I need separate insurance for each trip?',
  ],
};

const getPopularTopics = (category: string): string[] => {
  return TOPICS_BY_CATEGORY[category] || TOPICS_BY_CATEGORY['destinations'];
};

export const TravelQuestions: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isMobile = useIsMobile();
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const { addActivity, updateActivity, getActivityById, setCurrentActivityId } = useActivitiesStore();

  // Activity tracking state
  const [activityName, setActivityName] = useState('');
  const [currentActivityId, setLocalActivityId] = useState<string | null>(null);
  const [lastSavedHistoryLength, setLastSavedHistoryLength] = useState(0);

  // Form state
  const [category, setCategory] = useState('destinations');
  const [question, setQuestion] = useState('');
  const [travelStyle, setTravelStyle] = useState('mid-range');
  const [tripType, setTripType] = useState('solo');

  // UI state
  const [loading, setLoading] = useState(false);
  const [categories] = useState<TravelCategory[]>(defaultCategories);
  const [questionHistory, setQuestionHistory] = useState<QuestionResult[]>([]);
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initialQueryProcessed, setInitialQueryProcessed] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportIndex, setExportIndex] = useState<number | null>(null);

  // Handle initial query from navigation
  useEffect(() => {
    const state = location.state as { initialQuery?: string } | null;
    if (state?.initialQuery && !initialQueryProcessed) {
      setQuestion(state.initialQuery);
      setInitialQueryProcessed(true);
    }
  }, [location.state, initialQueryProcessed]);

  // Load activity from sidebar click
  useEffect(() => {
    const navState = location.state as { loadActivityId?: string } | null;
    if (navState?.loadActivityId) {
      const activity = getActivityById(navState.loadActivityId);
      if (activity && activity.activityType === 'travel-questions') {
        setActivityName(activity.name);
        const savedHistory = activity.data?.questionHistory || [];
        const convertedHistory = savedHistory.map((item: any) => ({
          ...item,
          timestamp: item.timestamp ? new Date(item.timestamp) : new Date(),
        }));
        setQuestionHistory(convertedHistory);
        if (convertedHistory.length > 0) {
          setActiveSessionId(convertedHistory[convertedHistory.length - 1].session_id);
        }
        setLocalActivityId(activity.id);
        setCurrentActivityId(activity.id);
        setLastSavedHistoryLength(savedHistory.length);
      }
      // Clear location state to prevent re-loading on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to top of results when new question is added
  useEffect(() => {
    if (questionHistory.length > 0) {
      resultsContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [questionHistory.length]);

  // Start a new conversation — keeps current activity saved, resets tracking for next query
  const startNewConversation = () => {
    setQuestionHistory([]);
    setActiveSessionId(null);
    setFollowUpQuestion('');
    setError(null);
    setActivityName('');
    setLocalActivityId(null);
    setCurrentActivityId(null);
    setLastSavedHistoryLength(0);
  };

  // When criteria change while an activity exists, finalize it and start fresh
  const handleCriteriaChange = (setter: (v: string) => void, value: string) => {
    setter(value);
    if (currentActivityId && questionHistory.length > 0) {
      startNewConversation();
    }
  };

  const resetForm = () => {
    setCategory('destinations');
    setQuestion('');
    setTravelStyle('mid-range');
    setTripType('solo');
    startNewConversation();
  };

  const handleAskQuestion = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!question.trim()) {
      setError(t('travelQuestions:errors.questionRequired', 'Please enter a question'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/travel-questions/ask', {
        category,
        question,
        travel_style: travelStyle,
        trip_type: tripType,
        simple_mode: true,
      });

      const data = response.data;

      const result: QuestionResult = {
        session_id: data.session_id || `session-${Date.now()}`,
        category,
        question,
        answer: data.answer,
        key_points: data.key_points || [],
        action_items: data.action_items || [],
        related_topics: data.related_topics || [],
        disclaimers: data.disclaimers || ['This information is for general guidance only. Travel conditions can change. Always verify with official sources.'],
        follow_up_suggestions: data.follow_up_suggestions || [],
        timestamp: new Date(),
      };

      const updatedHistory = [...questionHistory, result];
      setQuestionHistory(updatedHistory);
      setActiveSessionId(result.session_id);
      setQuestion('');

      // Auto-save activity
      let generatedName = activityName;
      if (!generatedName.trim()) {
        try {
          const titleResponse = await apiClient.post('/travel-questions/generate-title', { question });
          generatedName = titleResponse.data.title || question.substring(0, 50);
        } catch {
          generatedName = question.substring(0, 50);
        }
        setActivityName(generatedName);
      }

      const activityId = currentActivityId || `activity-${Date.now()}`;
      if (currentActivityId) {
        updateActivity(currentActivityId, {
          name: generatedName,
          savedAt: new Date(),
          data: { questionHistory: updatedHistory },
        });
      } else {
        addActivity({
          id: activityId,
          name: generatedName,
          activityType: 'travel-questions',
          savedAt: new Date(),
          data: { questionHistory: updatedHistory },
        });
        setLocalActivityId(activityId);
        setCurrentActivityId(activityId);
      }
      setLastSavedHistoryLength(updatedHistory.length);
    } catch (error) {
      console.error('Question error:', error);
      setError(t('travelQuestions:errors.askFailed', 'Failed to get answer. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUp = async (sessionId: string) => {
    if (!followUpQuestion.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const lastQuestion = questionHistory.find(q => q.session_id === sessionId);

      const response = await apiClient.post('/travel-questions/follow-up', {
        session_id: sessionId,
        question: followUpQuestion,
        context: lastQuestion?.answer.substring(0, 2000) || '',
      });

      const data = response.data;

      const result: QuestionResult = {
        session_id: sessionId,
        category: lastQuestion?.category || 'follow-up',
        question: `Follow-up: ${followUpQuestion}`,
        answer: data.response || data.answer,
        key_points: [],
        action_items: [],
        related_topics: [],
        disclaimers: [data.disclaimer || 'This information is for general guidance only.'],
        follow_up_suggestions: [],
        timestamp: new Date(),
      };

      const updatedHistory = [...questionHistory, result];
      setQuestionHistory(updatedHistory);
      setFollowUpQuestion('');

      // Auto-save activity
      if (currentActivityId) {
        updateActivity(currentActivityId, {
          savedAt: new Date(),
          data: { questionHistory: updatedHistory },
        });
        setLastSavedHistoryLength(updatedHistory.length);
      }
    } catch (error) {
      console.error('Follow-up error:', error);
      setError(t('travelQuestions:errors.followUpFailed', 'Failed to get follow-up response.'));
    } finally {
      setLoading(false);
    }
  };

  const deleteQuestion = (index: number) => {
    setQuestionHistory(questionHistory.filter((_, i) => i !== index));
  };

  const handlePrint = (result: QuestionResult) => {
    const categoryName = categories.find(c => c.id === result.category)?.name || result.category;
    const answerHtml = marked(result.answer, { async: false }) as string;
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Travel Question</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 40px 20px; color: #333; }
          h1 { text-align: center; color: #0891b2; border-bottom: 2px solid #0891b2; padding-bottom: 10px; }
          h2 { color: #0e7490; margin-top: 24px; }
          .meta { text-align: center; color: #666; margin-bottom: 24px; }
          ul, ol { margin: 8px 0; padding-left: 24px; }
          li { margin-bottom: 6px; }
          .disclaimer { margin-top: 24px; padding: 12px; background: #ecfeff; border-radius: 8px; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>Travel Question</h1>
        <div class="meta">
          <p>Date: ${result.timestamp.toLocaleString()}</p>
          <p>Category: ${categoryName}</p>
        </div>
        <h2>Your Question</h2>
        <p>${result.question}</p>
        <div>${answerHtml}</div>
        ${result.key_points.length > 0 ? `<h2>Key Points</h2><ul>${result.key_points.map(p => `<li>${p}</li>`).join('')}</ul>` : ''}
        ${result.action_items.length > 0 ? `<h2>Suggested Actions</h2><ul>${result.action_items.map(a => `<li>${a}</li>`).join('')}</ul>` : ''}
        <div class="disclaimer"><strong>Disclaimer:</strong> ${result.disclaimers[0]}</div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => { printWindow.print(); };
    }
  };

  const handleExport = async (format: 'print' | 'word' | 'pdf' | 'text', _scope: 'single' | 'all') => {
    if (exportIndex === null) return;
    const result = questionHistory[exportIndex];
    if (!result) return;

    if (format === 'print') {
      handlePrint(result);
      setExportDialogOpen(false);
      return;
    }

    try {
      const categoryName = categories.find(c => c.id === result.category)?.name || result.category;
      let content = `Travel Question\nDate: ${result.timestamp.toLocaleString()}\nCategory: ${categoryName}\n\n`;
      content += `### Question\n${result.question}\n\n${result.answer}\n\n`;
      if (result.key_points.length > 0) {
        content += `### Key Points\n`;
        result.key_points.forEach(p => content += `- ${p}\n`);
      }
      content += `\n### Disclaimer\n${result.disclaimers[0]}\n`;

      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `travel-question-${result.session_id.substring(0, 8)}.${extension}`;

      const blob = await exportAPI.exportDocument({
        content,
        title: 'Travel Question',
        format: extension as 'docx' | 'pdf' | 'txt',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      setError(t('travelQuestions:errors.exportFailed', 'Failed to export document'));
    }

    setExportDialogOpen(false);
  };

  return (
    <div className={`flex flex-col ${isMobile ? '' : 'h-full'} p-4 md:p-6`}>
      {/* Header */}
      <div className="text-center mb-4 md:mb-6">
        <div className="flex items-center justify-center mb-2 md:mb-4">
          <ChatBubbleLeftRightIcon className={`${isMobile ? 'h-8 w-8' : 'h-12 w-12'} text-cyan-500`} />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('travelQuestions:title', 'Ask Travel Questions')}
        </h1>
        <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1 md:mt-2">
          {t('travelQuestions:subtitle', 'Get AI-powered answers to your travel questions')}
        </p>
      </div>

      <div className={`flex-1 flex flex-col lg:flex-row gap-4 md:gap-6 max-w-7xl mx-auto w-full ${isMobile ? '' : 'overflow-hidden'}`}>
        {/* Left Panel - Options */}
        <div className={`lg:w-1/4 flex flex-col space-y-4 ${isMobile ? '' : 'overflow-y-auto'}`}>
          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('travelQuestions:form.category', 'Category')}
            </label>
            <select
              value={category}
              onChange={(e) => handleCriteriaChange(setCategory, e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500"
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            {category && categories.find(c => c.id === category) && (
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                {categories.find(c => c.id === category)?.description}
              </p>
            )}
          </div>

          {/* Travel Style */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('travelQuestions:form.travelStyle', 'Travel Style')}
            </label>
            <select
              value={travelStyle}
              onChange={(e) => handleCriteriaChange(setTravelStyle, e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500"
            >
              <option value="budget">{t('travelQuestions:travelStyles.budget', 'Budget')}</option>
              <option value="mid-range">{t('travelQuestions:travelStyles.midRange', 'Mid-Range')}</option>
              <option value="luxury">{t('travelQuestions:travelStyles.luxury', 'Luxury')}</option>
              <option value="backpacking">{t('travelQuestions:travelStyles.backpacking', 'Backpacking')}</option>
              <option value="business">{t('travelQuestions:travelStyles.business', 'Business')}</option>
            </select>
          </div>

          {/* Trip Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('travelQuestions:form.tripType', 'Trip Type')}
            </label>
            <select
              value={tripType}
              onChange={(e) => handleCriteriaChange(setTripType, e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500"
            >
              <option value="solo">{t('travelQuestions:tripTypes.solo', 'Solo')}</option>
              <option value="couple">{t('travelQuestions:tripTypes.couple', 'Couple')}</option>
              <option value="family">{t('travelQuestions:tripTypes.family', 'Family')}</option>
              <option value="group">{t('travelQuestions:tripTypes.group', 'Group')}</option>
              <option value="business">{t('travelQuestions:tripTypes.business', 'Business')}</option>
            </select>
          </div>

          {/* Popular Topics */}
          <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
            <h3 className="text-sm font-medium text-cyan-800 dark:text-cyan-200 mb-2">
              {t('travelQuestions:popularTopics', 'Popular Topics')}
            </h3>
            <div className="space-y-1">
              {getPopularTopics(category).map((topic, i) => (
                <button
                  key={i}
                  onClick={() => setQuestion(topic)}
                  className="block w-full text-left text-sm text-cyan-700 dark:text-cyan-300 hover:underline"
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>

          {/* Reset Button */}
          <button
            onClick={resetForm}
            className="w-full px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {t('travelQuestions:reset', 'Start New Session')}
          </button>
        </div>

        {/* Right Panel - Question & Answers */}
        <div className={`lg:w-3/4 flex flex-col bg-gray-50 dark:bg-gray-800 rounded-lg ${isMobile ? '' : 'overflow-hidden'}`}>
          {/* Question Input */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <form onSubmit={handleAskQuestion}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('travelQuestions:form.question', 'Your Question')}
              </label>
              <div className="relative">
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder={t('travelQuestions:form.questionPlaceholder', 'Ask any travel question...')}
                  rows={3}
                  className="w-full px-4 py-3 pr-12 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 resize-none"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !question.trim()}
                  className="absolute bottom-3 right-3 p-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  title={t('travelQuestions:form.ask', 'Ask')}
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
              )}
            </form>
          </div>

          {/* Question History */}
          <div ref={resultsContainerRef} className={`${isMobile ? '' : 'flex-1 overflow-y-auto'} p-4 space-y-6`}>
            {questionHistory.length === 0 ? (
              <div className="text-center py-12 text-gray-600 dark:text-gray-300">
                <LightBulbIcon className="h-16 w-16 mx-auto mb-4 opacity-60" />
                <p>{t('travelQuestions:noQuestions', 'No questions yet')}</p>
                <p className="text-sm mt-2 text-gray-500 dark:text-gray-400">
                  {t('travelQuestions:getStarted', 'Ask a question to get started')}
                </p>
              </div>
            ) : (
              [...questionHistory].reverse().map((result, reversedIndex) => {
                const actualIndex = questionHistory.length - 1 - reversedIndex;
                return (
                <div
                  key={`${result.session_id}-${actualIndex}`}
                  className="bg-white dark:bg-gray-700 rounded-lg shadow p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-cyan-100 dark:bg-cyan-900 text-cyan-800 dark:text-cyan-100 rounded">
                        {categories.find(c => c.id === result.category)?.name || result.category}
                      </span>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        {result.timestamp.toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setExportIndex(actualIndex);
                          setExportDialogOpen(true);
                        }}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                        title="Export"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                        </svg>
                      </button>
                      <button
                        onClick={() => deleteQuestion(actualIndex)}
                        className="p-2 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
                        title={t('travelQuestions:delete', 'Delete')}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      {t('travelQuestions:yourQuestion', 'Your Question')}:
                    </h4>
                    <p className="text-gray-700 dark:text-gray-200 text-sm italic">
                      {result.question}
                    </p>
                  </div>

                  <div className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ href, children }) => (
                          <a href={href} target="_blank" rel="noopener noreferrer"
                            className="text-cyan-600 dark:text-cyan-400 underline hover:text-cyan-700 dark:hover:text-cyan-300">
                            {children}
                          </a>
                        ),
                        strong: ({ children }) => (
                          <strong className="text-gray-900 dark:text-gray-100 font-semibold">{children}</strong>
                        ),
                        h3: ({ children }) => (
                          <h3 className="text-gray-900 dark:text-gray-100 text-base font-semibold mt-4 mb-2">{children}</h3>
                        ),
                        h4: ({ children }) => (
                          <h4 className="text-gray-900 dark:text-gray-100 text-sm font-semibold mt-3 mb-1">{children}</h4>
                        ),
                      }}
                    >
                      {result.answer}
                    </ReactMarkdown>
                  </div>

                  {result.key_points.length > 0 && (
                    <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
                        {t('travelQuestions:keyPoints', 'Key Points')}
                      </h4>
                      <ul className="list-disc list-inside space-y-1">
                        {result.key_points.map((point, i) => (
                          <li key={i} className="text-sm text-green-700 dark:text-green-300">
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.action_items.length > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                        {t('travelQuestions:actionItems', 'Suggested Actions')}
                      </h4>
                      <ul className="list-disc list-inside space-y-1">
                        {result.action_items.map((item, i) => (
                          <li key={i} className="text-sm text-blue-700 dark:text-blue-300">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.follow_up_suggestions.length > 0 && (
                    <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <h4 className="font-medium text-purple-800 dark:text-purple-200 mb-2">
                        {t('travelQuestions:suggestedFollowUps', 'Related Questions')}
                      </h4>
                      <ul className="space-y-1">
                        {result.follow_up_suggestions.map((suggestion, i) => (
                          <li
                            key={i}
                            className="text-sm text-purple-700 dark:text-purple-300 cursor-pointer hover:underline"
                            onClick={() => setFollowUpQuestion(suggestion)}
                          >
                            {suggestion}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-4 p-3 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded">
                    <p className="text-xs text-cyan-800 dark:text-cyan-200">
                      <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
                      {result.disclaimers[0]}
                    </p>
                  </div>
                </div>
              );})
            )}
          </div>

          {/* Follow-up Input */}
          {activeSessionId && questionHistory.length > 0 && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={followUpQuestion}
                  onChange={(e) => setFollowUpQuestion(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleFollowUp(activeSessionId)}
                  placeholder={t('travelQuestions:form.followUpPlaceholder', 'Ask a follow-up question...')}
                  className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  disabled={loading}
                />
                <button
                  onClick={() => handleFollowUp(activeSessionId)}
                  disabled={loading || !followUpQuestion.trim()}
                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? '...' : t('travelQuestions:form.askFollowUp', 'Ask')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <p className="mt-4 text-xs text-gray-400 dark:text-gray-500 text-center max-w-7xl mx-auto w-full">
        {t('travelQuestions:disclaimer.text', 'This tool provides general travel information for guidance purposes only. Travel conditions, regulations, and prices can change. Always verify details with official sources before traveling.')}
      </p>

      {/* Export Dialog */}
      <ExportDialog
        isOpen={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onExport={handleExport}
        hasMultipleResponses={false}
        documentType="Response"
        mode="both"
      />

      <LoadingOverlay isVisible={loading} message={t('travelQuestions:processing', 'Getting answer...')} />
    </div>
  );
};

export default TravelQuestions;
